// 交更 — open the till with a float, cash in / out, live expected cash, close
// by counting notes and coins, Z report. Expected cash also counts cash 收款
// taken on appointments outside the POS (server does the maths:
// pos_shift_figures), so the drawer reconciles however the money came in.
import { useCallback, useEffect, useMemo, useState } from "react";
import { zhEn, dateLocale } from "./posTier.js";
import { Lock, Unlock, ArrowDownToLine, ArrowUpFromLine, Printer, RefreshCw } from "lucide-react";
import { C, Btn, Chip, Pill, Card, Sheet, Label, Field, Stepper, Empty, money, round2 } from "./posUi.jsx";
import { posErrorText, newId } from "./posMath.js";
import { printShiftReport, payLabel } from "./posReceipt.js";
import { fetchOpenShift, fetchShifts, openShift, shiftCash, closeShift, shiftFigures } from "../../lib/api.js";

const DENOMS = [1000, 500, 100, 50, 20, 10, 5, 2, 1, 0.5, 0.2, 0.1];
const METHOD = { cash: zhEn("現金", "Cash"), wallet: zhEn("錢包", "Wallet"), points: zhEn("積分", "Points") };
const fmtTime = (iso) => iso ? new Date(iso).toLocaleString(dateLocale(), { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

export default function Shift({ tenant, sym, user, staffName, can, onShiftChange }) {
  const [open, setOpen] = useState(undefined);   // undefined = loading, null = none
  const [fig, setFig] = useState(null);
  const [history, setHistory] = useState([]);
  const [err, setErr] = useState("");
  const [float, setFloat] = useState("");
  const [busy, setBusy] = useState(false);
  const [move, setMove] = useState(null);         // 'in' | 'out'
  const [closing, setClosing] = useState(false);
  const [closed, setClosed] = useState(null);

  const load = useCallback(async () => {
    setErr("");
    try {
      const [o, h] = await Promise.all([fetchOpenShift(tenant.id), fetchShifts(tenant.id)]);
      setOpen(o); setHistory(h.filter(x => x.status === "closed"));
      setFig(o ? await shiftFigures(o.id) : null);
      onShiftChange?.(o);
    } catch (e) { setErr(posErrorText(e)); setOpen(o => o === undefined ? null : o); }
  }, [tenant.id, onShiftChange]);
  useEffect(() => { load(); }, [load]);

  const doOpen = async () => {
    setBusy(true); setErr("");
    try { await openShift({ tenantId: tenant.id, float: Number(float) || 0 }); setFloat(""); await load(); }
    catch (e) { setErr(posErrorText(e)); } finally { setBusy(false); }
  };

  if (open === undefined) return <p style={{ textAlign: "center", color: C.sub, fontWeight: 800, padding: 40 }}>{zhEn("載入中…", "Loading…")}</p>;

  return (
    <div style={{ maxWidth: 720 }}>
      {err && <p style={{ color: C.red, fontWeight: 800, fontSize: 13 }}>{err}</p>}

      {closed && (
        <Card style={{ padding: 16, marginBottom: 12, background: C.greenSoft, border: "none" }}>
          <p style={{ margin: 0, fontWeight: 900, color: "#047857", fontSize: 16 }}>{zhEn("✅ 已交更", "✅ Shift closed")}</p>
          <p style={{ margin: "4px 0 10px", fontWeight: 800, color: C.ink, fontSize: 14 }}>
            {zhEn("應有", "Expected")} {money(closed.expected_cash, sym)} {zhEn("· 點到", "· counted")} {money(closed.counted_cash, sym)} · <span style={{ color: Number(closed.difference) === 0 ? C.green : C.red }}>{Number(closed.difference) === 0 ? zhEn("啱數", "Balanced") : (Number(closed.difference) > 0 ? zhEn("多 ", "Over ") : zhEn("少 ", "Short ")) + money(Math.abs(closed.difference), sym)}</span>
          </p>
          <Btn kind="ghost" small onClick={() => printShiftReport(closed, { tenant, sym, staffName })}><Printer size={15} /> {zhEn("列印交更報告", "Print shift report")}</Btn>
        </Card>
      )}

      {!open ? (
        <Card style={{ padding: 18, marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <div style={{ width: 44, height: 44, borderRadius: 14, background: C.accentSoft, display: "flex", alignItems: "center", justifyContent: "center" }}><Unlock size={22} color={C.accent} /></div>
            <div>
              <p style={{ margin: 0, fontWeight: 900, color: C.ink, fontSize: 17 }}>{zhEn("開更", "Open shift")}</p>
              <p style={{ margin: 0, fontWeight: 700, color: C.sub, fontSize: 12.5 }}>{zhEn("點好錢櫃入面嘅找續錢（備用金）", "Count the change in the cash drawer (the float)")}</p>
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
            {[0, 300, 500, 1000, 2000].map(v => <Chip key={v} on={Number(float) === v && float !== ""} onClick={() => setFloat(String(v))}>{money(v, sym)}</Chip>)}
          </div>
          <Field label={zhEn("備用金", "Float")} inputMode="decimal" value={float} onChange={e => setFloat(e.target.value)} placeholder="0" />
          <Btn kind="green" style={{ width: "100%" }} disabled={busy} onClick={doOpen}>{busy ? zhEn("處理中…", "Processing…") : zhEn("開更 · 備用金 ", "Open shift · float ") + money(Number(float) || 0, sym)}</Btn>
        </Card>
      ) : (
        <>
          <Card style={{ padding: 18, marginBottom: 12, background: "linear-gradient(135deg,#FF9A9E,#FF758F)", border: "none" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ color: "rgba(255,255,255,0.9)", fontWeight: 900, fontSize: 13, flex: 1 }}>{zhEn("當更中 ·", "Shift open ·")} {fmtTime(open.opened_at)} {zhEn("開", "opened")}</span>
              <button type="button" onClick={load} aria-label={zhEn("重新整理", "Refresh")} style={{ border: "none", background: "rgba(255,255,255,0.22)", color: "#fff", borderRadius: 99, width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}><RefreshCw size={15} /></button>
            </div>
            <p style={{ margin: "6px 0 0", color: "rgba(255,255,255,0.85)", fontWeight: 900, fontSize: 13 }}>{zhEn("錢櫃應有", "Drawer should hold")}</p>
            <p style={{ margin: 0, color: "#fff", fontWeight: 900, fontSize: 38, letterSpacing: -1 }}>{money(fig?.expectedCash || 0, sym)}</p>
          </Card>
          {fig && (
            <Card style={{ padding: "6px 14px", marginBottom: 12 }}>
              <Row l={zhEn("備用金", "Float")} r={money(fig.openingFloat, sym)} />
              <Row l={zhEn("收銀現金（已扣找續）", "POS cash (after change given)")} r={money(fig.posCash, sym)} />
              {Number(fig.aptCash) !== 0 && <Row l={zhEn("預約收款現金（收銀以外）", "Booking cash (outside the POS)")} r={money(fig.aptCash, sym)} />}
              {Number(fig.cashIn) > 0 && <Row l={zhEn("放入現金", "Cash in")} r={"+" + money(fig.cashIn, sym)} />}
              {Number(fig.cashOut) > 0 && <Row l={zhEn("取出現金", "Cash out")} r={"−" + money(fig.cashOut, sym)} color={C.red} />}
              <Row l={zhEn("應有現金", "Expected cash")} r={money(fig.expectedCash, sym)} bold />
            </Card>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
            <Btn kind="ghost" onClick={() => setMove("in")}><ArrowDownToLine size={18} /> {zhEn("放入現金", "Cash in")}</Btn>
            <Btn kind="ghost" onClick={() => setMove("out")}><ArrowUpFromLine size={18} /> {zhEn("取出現金", "Cash out")}</Btn>
          </div>
          {fig && (
            <Card style={{ padding: 14, marginBottom: 12 }}>
              <Label>{zhEn("今更銷售", "This shift's sales")}</Label>
              <Row l={zhEn("單數", "Sales")} r={fig.sales} />
              <Row l={zhEn("淨銷售", "Net sales")} r={money(fig.netSales, sym)} />
              {Number(fig.refunds) > 0 && <Row l={zhEn("退款", "Refund")} r={"−" + money(fig.refunds, sym)} color={C.red} />}
              {Object.entries(fig.byMethod || {}).map(([k, v]) => <Row key={k} l={"· " + payLabel(METHOD[k] || k)} r={money(v, sym)} />)}
            </Card>
          )}
          {(open.cash_moves || []).length > 0 && (
            <Card style={{ padding: 14, marginBottom: 12 }}>
              <Label>{zhEn("現金出入記錄", "Cash in / out log")}</Label>
              {open.cash_moves.map(m => (
                <div key={m.id} style={{ display: "flex", gap: 8, padding: "6px 0", borderBottom: "1px solid " + C.line, fontSize: 13, fontWeight: 800 }}>
                  <span style={{ color: C.sub }}>{fmtTime(m.at)}</span>
                  <span style={{ flex: 1, color: C.ink }}>{m.note || (m.type === "in" ? zhEn("放入", "In") : zhEn("取出", "Out"))}</span>
                  <span style={{ color: m.type === "in" ? C.green : C.red }}>{m.type === "in" ? "+" : "−"}{money(m.amount, sym)}</span>
                </div>
              ))}
            </Card>
          )}
          {can("closeShift")
            ? <Btn style={{ width: "100%", padding: 16, background: C.ink, boxShadow: "none" }} onClick={() => setClosing(true)}><Lock size={18} /> {zhEn("交更 · 點錢", "Close shift · count cash")}</Btn>
            : <p style={{ textAlign: "center", fontWeight: 800, color: C.sub, fontSize: 13 }}>{zhEn("交更需要店主操作", "Closing the shift needs the owner")}</p>}
        </>
      )}

      <Label style={{ marginTop: 18 }}>{zhEn("過往交更", "Past shifts")}</Label>
      {history.length === 0 ? <Empty icon="🗄️" title={zhEn("未有交更記錄", "No shifts yet")} />
        : history.map(h => (
          <Card key={h.id} onClick={() => printShiftReport(h, { tenant, sym })} style={{ padding: "10px 14px", marginBottom: 8, display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontWeight: 900, color: C.ink, fontSize: 14 }}>{fmtTime(h.opened_at)} → {fmtTime(h.closed_at)}</p>
              <p style={{ margin: 0, fontWeight: 700, color: C.sub, fontSize: 12 }}>{zhEn("應有", "Expected")} {money(h.expected_cash, sym)} {zhEn("· 點到", "· counted")} {money(h.counted_cash, sym)} {zhEn("· 淨銷售", "· net sales")} {money(h.summary?.netSales || 0, sym)}</p>
            </div>
            <Pill tone={Number(h.difference) === 0 ? "green" : "red"}>{Number(h.difference) === 0 ? zhEn("啱數", "Balanced") : (Number(h.difference) > 0 ? "+" : "−") + money(Math.abs(h.difference), sym)}</Pill>
            <Printer size={16} color={C.faint} />
          </Card>
        ))}

      <CashMoveSheet type={move} sym={sym} onClose={() => setMove(null)}
        onSave={async (amount, note) => { await shiftCash({ shiftId: open.id, id: newId(), type: move, amount, note }); setMove(null); await load(); }} />
      {closing && open && fig && (
        <CloseSheet sym={sym} expected={Number(fig.expectedCash) || 0} onClose={() => setClosing(false)}
          onConfirm={async (counted, note) => {
            const row = await closeShift({ shiftId: open.id, counted, note });
            setClosing(false); setClosed(row); await load();
          }} />
      )}
    </div>
  );
}

const Row = ({ l, r, color, bold }) => (
  <div style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid " + C.line, fontSize: bold ? 16 : 14, fontWeight: bold ? 900 : 800, color: color || C.ink }}>
    <span style={{ color: bold ? C.ink : C.sub }}>{l}</span><span>{r}</span>
  </div>
);

function CashMoveSheet({ type, sym, onClose, onSave }) {
  const [amt, setAmt] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  if (!type) return null;
  const a = round2(Number(amt) || 0);
  return (
    <Sheet open onClose={onClose} title={type === "in" ? zhEn("放入現金", "Cash in") : zhEn("取出現金", "Cash out")}
      footer={<div>{err && <p style={{ color: C.red, fontWeight: 800, fontSize: 13, textAlign: "center", margin: "0 0 8px" }}>{err}</p>}
        <Btn style={{ width: "100%" }} disabled={a <= 0 || busy} onClick={async () => { setBusy(true); setErr(""); try { await onSave(a, note.trim()); setAmt(""); setNote(""); } catch (e) { setErr(posErrorText(e)); } finally { setBusy(false); } }}>{type === "in" ? zhEn("放入 ", "Put in ") : zhEn("取出 ", "Take out ")}{money(a, sym)}</Btn></div>}>
      <Field label={zhEn("金額", "Amount")} inputMode="decimal" value={amt} onChange={e => setAmt(e.target.value)} autoFocus />
      <Label>{zhEn("原因", "Reason")}</Label>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
        {(type === "in" ? [zhEn("補找續錢", "Top up change"), zhEn("其他", "Other")] : [zhEn("買雜物", "Supplies"), zhEn("存入銀行", "Bank deposit"), zhEn("員工飯錢", "Staff meals"), zhEn("其他", "Other")]).map(x => <Chip key={x} on={note === x} onClick={() => setNote(x)}>{x}</Chip>)}
      </div>
      <Field value={note} onChange={e => setNote(e.target.value)} placeholder={zhEn("或者自己寫", "Or write your own")} />
    </Sheet>
  );
}

// Count notes + coins (or type a total), see the difference, confirm.
function CloseSheet({ sym, expected, onClose, onConfirm }) {
  const [counts, setCounts] = useState({});
  const [manual, setManual] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const counted = useMemo(() => manual !== "" ? round2(Number(manual) || 0) : round2(DENOMS.reduce((s, d) => s + d * (counts[d] || 0), 0)), [counts, manual]);
  const diff = round2(counted - expected);
  return (
    <Sheet open onClose={busy ? undefined : onClose} title={zhEn("交更 · 點錢", "Close shift · count cash")}
      footer={<div>
        {err && <p style={{ color: C.red, fontWeight: 800, fontSize: 13, textAlign: "center", margin: "0 0 8px" }}>{err}</p>}
        <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 900, fontSize: 15, color: C.ink, marginBottom: 8 }}>
          <span>{zhEn("點到", "Counted")} {money(counted, sym)}</span>
          <span style={{ color: diff === 0 ? C.green : C.red }}>{diff === 0 ? zhEn("啱數 ✓", "Balanced ✓") : (diff > 0 ? zhEn("多 ", "Over ") : zhEn("少 ", "Short ")) + money(Math.abs(diff), sym)}</span>
        </div>
        <Btn style={{ width: "100%", background: C.ink, boxShadow: "none" }} disabled={busy} onClick={async () => {
          if (diff !== 0 && !note.trim() && !window.confirm(zhEn("數目", "The count is ") + (diff > 0 ? zhEn("多咗 ", "over by ") : zhEn("少咗 ", "short by ")) + money(Math.abs(diff), sym) + zhEn("，冇寫原因，照樣交更？", " — no reason given. Close the shift anyway?"))) return;
          setBusy(true); setErr(""); try { await onConfirm(counted, note.trim()); } catch (e) { setErr(posErrorText(e)); setBusy(false); }
        }}>{busy ? zhEn("處理中…", "Processing…") : zhEn("確認交更", "Confirm close")}</Btn>
      </div>}>
      <p style={{ margin: "0 2px 12px", fontWeight: 800, color: C.sub, fontSize: 13 }}>{zhEn("系統應有", "System expects")} {money(expected, sym)} {zhEn("— 逐種面額數一數", "— count each denomination")}</p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12, opacity: manual !== "" ? 0.4 : 1 }}>
        {DENOMS.map(d => (
          <div key={d} style={{ display: "flex", alignItems: "center", gap: 6, background: "#fff", border: "1px solid " + C.line, borderRadius: 14, padding: "6px 8px" }}>
            <span style={{ flex: 1, fontWeight: 900, color: C.ink, fontSize: 13 }}>{d < 1 ? (d * 100) + "¢" : "$" + d}</span>
            <Stepper value={counts[d] || 0} min={0} onChange={v => { setManual(""); setCounts(c => ({ ...c, [d]: v })); }} />
          </div>
        ))}
      </div>
      <Field label={zhEn("或者直接輸入總數", "Or enter the total directly")} inputMode="decimal" value={manual} onChange={e => setManual(e.target.value)} placeholder={String(counted)} />
      <Field label={zhEn("備註（有差額請寫原因）", "Note (give a reason if there's a difference)")} value={note} onChange={e => setNote(e.target.value)} />
    </Sheet>
  );
}
